import React, { useEffect, useState, useCallback } from "react";
import api, { apiError, inr, API } from "@/lib/api";
import { offerText } from "@/components/PriceTag";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HolyPicker } from "@/components/PackagePhotos";
import { holySrc } from "@/lib/holyPhotos";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Plus, Pencil, Trash2, Users, X, Check, FileSpreadsheet } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; }
};

export default function Groups() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const token = localStorage.getItem("token");
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [muallems, setMuallems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", package: "", seats: "", muallem_id: "", photo: "", price: "" });
  const [picker, setPicker] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assignSel, setAssignSel] = useState({});

  const load = useCallback(async () => {
    const [g, c, p, w, mu] = await Promise.all([api.get("/groups"), api.get("/contacts"), api.get("/pricing").catch(() => ({ data: { packages: [] } })), api.get("/waitlist").catch(() => ({ data: [] })), api.get("/muallems").catch(() => ({ data: [] }))]);
    setGroups(g.data);
    setContacts(c.data);
    setPackages(p.data.packages || []);
    setWaitlist(w.data);
    setMuallems(mu.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearWaitlist = async (wid) => {
    try {
      await api.delete(`/waitlist/${wid}`);
      toast.success("Waitlist entry cleared");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const confirmSeat = async (wid) => {
    try {
      const { data } = await api.post(`/waitlist/${wid}/confirm`);
      toast.success(data.message || "Seat confirmed");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditId(null); setForm({ name: "", date: "", package: "", seats: "", muallem_id: "", photo: "", price: "", mrp: "" }); setOpen(true); };
  const openEdit = (g) => { setEditId(g.id); setForm({ name: g.name, date: g.date, package: g.package || "", seats: g.seats ? String(g.seats) : "", muallem_id: g.muallem_id || "", photo: g.photo || "", price: g.own_price ? String(g.own_price) : "", mrp: g.own_mrp ? String(g.own_mrp) : "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Group name and date are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, seats: form.seats ? Number(form.seats) : 0, price: form.price ? Number(form.price) : 0, mrp: form.mrp ? Number(form.mrp) : 0, muallem_id: form.muallem_id || null };
      if (editId) await api.put(`/groups/${editId}`, payload);
      else await api.post("/groups", payload);
      toast.success(editId ? "Group updated" : "Group created");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeGroup = async (id) => {
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Group removed — its customers are now ungrouped");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const assign = async (contactId, groupId) => {
    const c = contacts.find((x) => x.id === contactId);
    if (!c) return;
    try {
      await api.put(`/contacts/${contactId}`, {
        workspace_id: localStorage.getItem("workspace_id"),
        name: c.name, phone: c.phone, email: c.email || "", tags: c.tags || [], agent_id: c.agent_id,
        group_id: groupId,
      });
      toast.success(groupId ? "Customer added to group" : "Customer removed from group");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="groups-page">
      <PageHeader eyebrow="Travel" title="Groups & Dates"
        subtitle="Admin creates groups with travel dates — staff assign their customers to a group"
        action={isOwner ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-group-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Group</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20" data-testid="group-modal">
              <DialogHeader><DialogTitle className="font-serif text-gold flex items-center gap-2"><CalendarDays className="w-5 h-5" /> {editId ? "Edit Group" : "New Group"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Group name</Label><Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Umrah Group A" /></div>
                <div><Label className="text-xs text-muted-foreground">Travel date</Label><Input data-testid="group-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-card/60" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Linked package</Label>
                  <Select value={form.package || "__none__"} onValueChange={(v) => setForm({ ...form, package: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="group-package-select" className="mt-1 bg-card/60 border-border/60">
                      <SelectValue placeholder="Choose a package" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#05261D] border-gold/20">
                      <SelectItem value="__none__">No package</SelectItem>
                      {packages.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} — {inr(p.price)}{p.mrp > p.price ? ` (was ${inr(p.mrp)})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Customers added to this group get this package automatically.</p>
                </div>
                <div><Label className="text-xs text-muted-foreground">Package price per person (₹)</Label><Input data-testid="group-price-input" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 bg-card/60" placeholder={packages.find((p) => p.name === form.package) ? `Default ${packages.find((p) => p.name === form.package).price}` : "e.g. 95000"} /><p className="text-[10px] text-muted-foreground mt-1">Shown to company, members and customers. Leave empty to use the linked package price.</p></div>
                <div><Label className="text-xs text-muted-foreground">Original price before discount (₹)</Label><Input data-testid="group-mrp-input" type="number" min="0" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. 110000 (shown crossed out)" /><p className="text-[10px] text-muted-foreground mt-1">Leave empty to use the package's full price.</p></div>
                <div><Label className="text-xs text-muted-foreground">Total seats (0 = unlimited)</Label><Input data-testid="group-seats-input" type="number" min="0" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. 40" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Group leader (Muallem)</Label>
                  <Select value={form.muallem_id || "__none__"} onValueChange={(v) => setForm({ ...form, muallem_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="group-muallem-select" className="mt-1 bg-card/60 border-border/60"><SelectValue placeholder="Choose a muallem" /></SelectTrigger>
                    <SelectContent className="bg-[#05261D] border-gold/20">
                      <SelectItem value="__none__">No muallem</SelectItem>
                      {muallems.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Group photo (Makkah / Madinah)</Label>
                  <button type="button" data-testid="group-photo-pick" onClick={() => setPicker(true)}
                    className="mt-1 w-full h-20 rounded-md border border-dashed border-gold/50 overflow-hidden flex items-center justify-center text-gold text-xs hover:bg-gold/10">
                    {form.photo ? <img src={holySrc(form.photo)} alt="group" className="w-full h-full object-cover" /> : "Choose photo"}
                  </button>
                  <HolyPicker open={picker} onOpenChange={setPicker} selected={form.photo} onPick={(k) => { setForm({ ...form, photo: k }); setPicker(false); }} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-group-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Group"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      />
      <div className="p-4 sm:p-8 grid sm:grid-cols-2 gap-4">
        {groups.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-12">No groups yet. {isOwner ? "Add your first group with a travel date." : "The admin will add groups here."}</p>}
        {groups.map((g) => {
          const members = contacts.filter((c) => c.group_id === g.id);
          const unassigned = contacts.filter((c) => !c.group_id);
          return (
            <Card key={g.id} data-testid={`group-card-${g.id}`} className="p-4 bg-card/40 border-border/60 overflow-hidden">
              {g.photo && <img src={holySrc(g.photo)} alt={g.name} className="-mx-4 -mt-4 mb-3 h-28 w-[calc(100%+2rem)] max-w-none object-cover" />}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-serif font-semibold text-white flex items-center gap-2"><CalendarDays className="w-4 h-4 text-gold" /> {g.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-gold text-xs">{fmtDate(g.date)}</p>
                    {g.package && <Badge variant="outline" data-testid={`group-package-badge-${g.id}`} className="border-gold/40 text-gold text-[10px]">{g.package}</Badge>}
                    {g.price > 0 && <Badge data-testid={`group-price-badge-${g.id}`} className="bg-gold text-[#0a2e22] text-[10px] font-bold">{inr(g.price)}{offerText(g.price, g.mrp, g.offer_until) && <span className="ml-1 line-through opacity-60">{inr(g.mrp)}</span>}</Badge>}
                    {offerText(g.price, g.mrp, g.offer_until) && <Badge data-testid={`group-offer-badge-${g.id}`} className="bg-red-500/85 text-white text-[10px] font-bold">{offerText(g.price, g.mrp, g.offer_until)}</Badge>}
                    {g.muallem_id && muallems.find((m) => m.id === g.muallem_id) && (() => {
                      const mu = muallems.find((m) => m.id === g.muallem_id);
                      return (
                        <span data-testid={`group-muallem-badge-${g.id}`} className="flex items-center gap-1.5 border border-emerald-400/40 rounded-full pl-0.5 pr-2 py-0.5">
                          {mu.photo_path ? (
                            <img src={`${API}/muallems/${mu.id}/photo?auth=${token}`} alt={mu.name} className="w-5 h-5 rounded-full object-cover ring-1 ring-emerald-400/50" />
                          ) : (
                            <span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-[9px] font-serif text-emerald-300">{mu.name[0]}</span>
                          )}
                          <span className="text-emerald-300 text-[10px]">Muallem: {mu.name}</span>
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" /> {g.seats ? `${members.length} / ${g.seats} seats filled` : `${members.length} customer${members.length === 1 ? "" : "s"}`}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <a href={`${API}/exports/group-customers?workspace_id=${localStorage.getItem("workspace_id")}&group_id=${g.id}&auth=${localStorage.getItem("token")}`} data-testid={`group-excel-${g.id}`} title="Download customer list (Excel)">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-300 hover:bg-emerald-500/15"><FileSpreadsheet className="w-3.5 h-3.5" /></Button>
                  </a>
                  {isOwner && (
                    <>
                      <Button size="icon" variant="ghost" data-testid={`group-edit-${g.id}`} onClick={() => openEdit(g)} className="h-8 w-8 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" data-testid={`group-delete-${g.id}`} onClick={() => removeGroup(g.id)} className="h-8 w-8 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>

              {members.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {members.map((m) => (
                    <div key={m.id} data-testid={`group-member-${m.id}`} className="flex items-center justify-between text-xs bg-card/60 rounded-md px-2.5 py-1.5">
                      <span className="truncate">{m.name} <span className="text-muted-foreground font-mono">{m.phone}</span></span>
                      <button data-testid={`group-remove-${m.id}`} onClick={() => assign(m.id, "")} className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}

              {waitlist.filter((w) => w.group_id === g.id).length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid={`group-waitlist-${g.id}`}>
                  <p className="eyebrow text-amber-300">Waitlist ({waitlist.filter((w) => w.group_id === g.id).length}) — request more seats</p>
                  {waitlist.filter((w) => w.group_id === g.id).map((w) => (
                    <div key={w.id} data-testid={`waitlist-row-${w.id}`} className="flex items-center justify-between text-xs bg-amber-500/10 border border-amber-400/20 rounded-md px-2.5 py-1.5">
                      <span className="truncate">{w.name} <span className="text-muted-foreground font-mono">{w.phone}</span></span>
                      {isOwner && (
                        <span className="flex gap-1.5 flex-shrink-0">
                          <button data-testid={`waitlist-confirm-${w.id}`} onClick={() => confirmSeat(w.id)} title="Confirm seat" className="text-emerald-300 hover:text-emerald-200 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                          <button data-testid={`waitlist-clear-${w.id}`} onClick={() => clearWaitlist(w.id)} title="Remove" className="text-muted-foreground hover:text-destructive transition-colors"><X className="w-3.5 h-3.5" /></button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3">
                <Select value={assignSel[g.id] || ""} onValueChange={(v) => { setAssignSel({ ...assignSel, [g.id]: v }); assign(v, g.id); }}>
                  <SelectTrigger data-testid={`group-assign-${g.id}`} className="bg-card/60 border-border/60 text-xs h-9">
                    <SelectValue placeholder="+ Add your customer to this group" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#05261D] border-gold/20 max-h-56">
                    {unassigned.length === 0 && <div className="p-2 text-xs text-muted-foreground">No unassigned customers</div>}
                    {unassigned.map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name} · {c.phone}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
